import { useTacoConfigParameter } from '@coursera-web/taco-config-client';

import { isActivityInjectionApiEnabled, isActivityInjectionUiEnabled, previewActivityInjectionVariant } from '../utils';

export const TACO_NAMESPACE_INJECTABLE_ACTIVITIES_ROLLOUT_ALLOWLIST = 'injectable-activities/rollout-allowlist';
export const TACO_PARAMETER_INJECTABLE_ACTIVITIES_ENABLED_COURSE_IDS = 'enabledCourseIds';

/**
 * Hook to check if a given course is whitelisted for Proactive AI injection
 * (using TACO configuration). The hook also checks Epic feature flags to
 * separately guard the API calls and UI display during development and testing.
 *
 * @param courseId - The id of the course to check if injection is enabled for.
 * @returns Object with isApiEnabled, isUiEnabled booleans, loading state, and error.
 */
export const useIsInjectionEnabledForCourse = (
  courseId: string
): {
  isApiEnabled: boolean;
  isUiEnabled: boolean;
  loading: boolean;
  error?: string;
} => {
  const isApiFeatureEnabled = isActivityInjectionApiEnabled();
  const isUiFeatureEnabled = isActivityInjectionUiEnabled();

  // handle not targeted by epic experiment OR opted out explicitly.
  const variant = previewActivityInjectionVariant();
  const isTargetedByVariant = variant === 'CONTROL' || variant === 'STATIC' || variant === 'INTERACTIVE';

  const {
    data: enabledCourseIds,
    loading,
    error,
  } = useTacoConfigParameter<string[]>(
    TACO_NAMESPACE_INJECTABLE_ACTIVITIES_ROLLOUT_ALLOWLIST,
    TACO_PARAMETER_INJECTABLE_ACTIVITIES_ENABLED_COURSE_IDS
  );

  const isCourseAllowedByTaco = (enabledCourseIds || []).includes(courseId);
  const isApiEnabled = isApiFeatureEnabled && isTargetedByVariant && isCourseAllowedByTaco;
  const isUiEnabled = isUiFeatureEnabled && isTargetedByVariant && isCourseAllowedByTaco;

  return { isApiEnabled, isUiEnabled, loading: isApiFeatureEnabled && loading, error };
};
